import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import {
  getEroundCatalogue,
  listModelMappings,
  listUnmappedModels,
  saveModelMapping,
} from "../../../services/eroundProvisioning";
import { getProspect } from "../../../services/onboarding/prospects";
import { ProspectDetail } from "../../../types/onboarding";
import { listWallbox } from "../../../services/onboarding/wallbox";
import {
  BlockedStation,
  CatalogueManufacturer,
  CatalogueModel,
  EroundCatalogue,
  ModelMapping,
  ModelMappingInput,
  UnmappedModel,
} from "../../../types/eroundProvisioning";
import { customerName } from "../../../utils/customerName";
import { connectorSummary, isIntegrated } from "../../../utils/eroundCatalogue";
import { Suggestion, WallboxHints, hintsFromProspect } from "../../../utils/eroundSuggestions";
import { modelKey } from "../../../utils/modelKey";
import { isStandIn } from "../../../utils/standIn";
import { provisioningErrorMessage } from "../Prospects/components/provisioningErrors";
import ModelSuggestions, { suggestedReason } from "./ModelSuggestions";

export type Notice = { severity: "success" | "error"; text: string };
// A suggestion support chose to use; seq tells two clicks on the same one apart.
type Preset = { suggestion: Suggestion; seq: number };

const isValid = (input: ModelMappingInput): boolean =>
  Boolean(input.eroundManufacturer.trim() && input.eroundModel.trim()) &&
  Number.isInteger(input.chargingPoints) &&
  input.chargingPoints >= 1;

const ManufacturerOption: React.FC<{ manufacturer: CatalogueManufacturer }> = ({ manufacturer }) => (
  <span>
    {manufacturer.name}
    {manufacturer.marking && <small className="ml-2 text-orange-600">{manufacturer.marking}</small>}
  </span>
);

const ModelOption: React.FC<{ model: CatalogueModel }> = ({ model }) => (
  <div>
    <div>{model.name}</div>
    <small className="text-600">
      {connectorSummary(model)}
      {!isIntegrated(model) && <span className="text-orange-600"> · Not integrated</span>}
    </small>
  </div>
);

// eRound's names come from its own catalogue (SOPS-180), so a mapping can only name a model that exists.
export const MappingForm: React.FC<{
  model: UnmappedModel;
  catalogue: EroundCatalogue | null;
  preset: Preset | null;
  onSaved: (notice: Notice) => void;
}> = ({ model, catalogue, preset, onSaved }) => {
  const [input, setInput] = useState<ModelMappingInput>({ eroundManufacturer: "", eroundModel: "", chargingPoints: 1 });
  // A stand-in records why the wallbox runs as this model, and who at eRound agreed (SOPS-181).
  const [standIn, setStandIn] = useState({ reason: "", supportReference: "" });
  const [saving, setSaving] = useState(false);
  const id = (field: string) => `${model.modelKey}-${field}`;
  const manufacturer = catalogue?.manufacturers.find((m) => m.name === input.eroundManufacturer) ?? null;
  const chosen = manufacturer?.models.find((m) => m.name === input.eroundModel) ?? null;
  // SOPS-195: no checkbox. Another model than the customer's own is a stand-in, and needs only why it fits.
  const standInOn =
    chosen !== null && isStandIn(model.onboardingManufacturer, model.onboardingModel, input.eroundManufacturer, input.eroundModel);
  const standInComplete = !standInOn || Boolean(standIn.reason.trim());

  const pickManufacturer = (name: string) => {
    const models = catalogue?.manufacturers.find((m) => m.name === name)?.models ?? [];
    setInput((prev) => ({
      ...prev,
      eroundManufacturer: name,
      eroundModel: models.some((m) => m.name === prev.eroundModel) ? prev.eroundModel : "",
    }));
  };

  const pickModel = (name: string) => {
    const picked = manufacturer?.models.find((m) => m.name === name);
    setInput((prev) => ({
      ...prev,
      eroundModel: name,
      chargingPoints: picked?.connectors.length || prev.chargingPoints,
    }));
  };

  // A suggestion only fills the form (SOPS-187), including why it fits, which a stand-in needs (SOPS-195).
  useEffect(() => {
    if (!preset) return;
    const { suggestion } = preset;
    setInput({
      eroundManufacturer: suggestion.manufacturer.name,
      eroundModel: suggestion.model.name,
      chargingPoints: suggestion.model.connectors.length,
    });
    setStandIn((prev) => ({ reason: suggestedReason(suggestion), supportReference: prev.supportReference }));
  }, [preset]);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveModelMapping(
        model,
        standInOn ? { ...input, standIn: { reason: standIn.reason, supportReference: standIn.supportReference } } : input
      );
      const count = saved.reevaluatedStationIds.length;
      onSaved({ severity: "success", text: `Saved. ${count} station${count === 1 ? "" : "s"} re-evaluated.` });
    } catch (err) {
      onSaved({ severity: "error", text: provisioningErrorMessage(err, "map") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid formgrid p-fluid">
      <div className="field col-12 md:col-4">
        <label htmlFor={id("manufacturer")}>eRound manufacturer</label>
        <Dropdown
          inputId={id("manufacturer")}
          value={input.eroundManufacturer || null}
          options={catalogue?.manufacturers ?? []}
          optionLabel="name"
          optionValue="name"
          itemTemplate={(m: CatalogueManufacturer) => <ManufacturerOption manufacturer={m} />}
          filter
          placeholder="Search eRound's manufacturers"
          disabled={!catalogue}
          onChange={(e) => pickManufacturer(e.value)}
        />
      </div>
      <div className="field col-12 md:col-5">
        <label htmlFor={id("model")}>eRound model</label>
        <Dropdown
          inputId={id("model")}
          value={input.eroundModel || null}
          options={manufacturer?.models ?? []}
          optionLabel="name"
          optionValue="name"
          itemTemplate={(m: CatalogueModel) => <ModelOption model={m} />}
          filter
          placeholder={manufacturer ? `Search ${manufacturer.name}'s models` : "Pick a manufacturer first"}
          disabled={!manufacturer}
          onChange={(e) => pickModel(e.value)}
        />
        {chosen && <small className="text-600">{connectorSummary(chosen)}</small>}
      </div>
      <div className="field col-12 md:col-3">
        <label htmlFor={id("points")}>Charging points</label>
        <InputNumber
          inputId={id("points")}
          value={input.chargingPoints}
          min={1}
          useGrouping={false}
          onValueChange={(e) => setInput({ ...input, chargingPoints: e.value ?? 0 })}
        />
      </div>
      {standInOn && (
        <>
          <div className="col-12 mb-2 text-700">
            {`Stand-in: eRound doesn't list this wallbox, so its stations run as ${input.eroundManufacturer} ${input.eroundModel}.`}
          </div>
          <div className="field col-12 md:col-7">
            <label htmlFor={id("stand-in-reason")}>Why it fits</label>
            <InputTextarea
              id={id("stand-in-reason")}
              rows={2}
              autoResize
              value={standIn.reason}
              placeholder="e.g. Type 2 socket, 22 kW, 3-phase, like the model above"
              onChange={(e) => setStandIn({ ...standIn, reason: e.target.value })}
            />
          </div>
          <div className="field col-12 md:col-5">
            <label htmlFor={id("stand-in-reference")}>eRound support reference (optional)</label>
            <InputText
              id={id("stand-in-reference")}
              value={standIn.supportReference}
              placeholder="e.g. eRound ticket and who agreed"
              onChange={(e) => setStandIn({ ...standIn, supportReference: e.target.value })}
            />
          </div>
        </>
      )}
      <div className="col-12 flex justify-content-end">
        <Button
          label="Save"
          className="w-auto"
          loading={saving}
          disabled={saving || !chosen || !isValid(input) || !standInComplete}
          onClick={save}
        />
      </div>
    </div>
  );
};

const formatDay = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "an unknown date" : d.toLocaleDateString();
};

// The prospect behind each blocked station, for its name and the wallbox specs it entered (CWA-229).
type Customers = Map<string, ProspectDetail | null>;

interface CustomerGroup {
  prospectId: string | null;
  name: string | null;
  stationIds: string[];
}

// One entry per customer, in station order; stations whose customer is unknown stay plain IDs (SOPS-179).
const groupByCustomer = (stations: BlockedStation[], customers: Customers): CustomerGroup[] =>
  stations.reduce<CustomerGroup[]>((groups, station) => {
    const name = station.prospectId ? customerName(customers.get(station.prospectId)) : null;
    const prospectId = name ? station.prospectId : null;
    const existing = prospectId
      ? groups.find((g) => g.prospectId === prospectId)
      : groups.length > 0 && groups[groups.length - 1].prospectId === null
        ? groups[groups.length - 1]
        : undefined;
    if (existing) existing.stationIds.push(station.stationId);
    else groups.push({ prospectId, name, stationIds: [station.stationId] });
    return groups;
  }, []);

const BlockedCustomers: React.FC<{ stations: BlockedStation[]; customers: Customers }> = ({ stations, customers }) => (
  <span>
    Blocks{" "}
    {groupByCustomer(stations, customers).map((group, i) => (
      <React.Fragment key={group.prospectId ?? `ids-${i}`}>
        {i > 0 && " · "}
        {group.prospectId && group.name ? (
          <>
            <Link to={`/onboarding/prospects/${group.prospectId}`}>{group.name}</Link> ({group.stationIds.join(", ")})
          </>
        ) : (
          group.stationIds.join(", ")
        )}
      </React.Fragment>
    ))}
  </span>
);

// Each prospect once per load, however many models block it.
const loadCustomers = async (models: UnmappedModel[]): Promise<Customers> => {
  const ids = Array.from(
    new Set(models.flatMap((m) => m.blockedStations.map((s) => s.prospectId)).filter((id): id is string => !!id))
  );
  return new Map(
    await Promise.all(ids.map(async (id) => [id, await getProspect(id).catch(() => null)] as const))
  );
};

// The first blocked station whose customer entered the unlisted wallbox's specs (CWA-229).
const enteredHints = (model: UnmappedModel, customers: Customers): WallboxHints | null => {
  for (const station of model.blockedStations) {
    const prospect = station.prospectId ? customers.get(station.prospectId) : null;
    const hints = prospect ? hintsFromProspect(prospect, station.stationId) : null;
    if (hints) return hints;
  }
  return null;
};

// A catalogue model's eRound mapping lives on its catalogue entry (SOPS-169);
// mapping it here as well would leave two mappings to disagree.
const catalogueKeys = async (): Promise<Set<string>> => {
  try {
    const data = await listWallbox();
    const keys = (data?.entries ?? []).map((e) => modelKey(e.manufacturer, e.model_string));
    return new Set(keys.filter((key): key is string => key !== null));
  } catch (err) {
    console.error("Failed to load the wallbox catalogue", err);
    return new Set();
  }
};

/** Unblocks stations whose wallbox model has no eRound names recorded yet (SOPS-148). */
const UnmappedModelsPanel: React.FC = () => {
  const [models, setModels] = useState<UnmappedModel[] | null>(null);
  const [inCatalogue, setInCatalogue] = useState<Set<string>>(new Set());
  const [customers, setCustomers] = useState<Customers>(new Map());
  // undefined while loading; null when the worker has not sent one yet or it could not be loaded.
  const [catalogue, setCatalogue] = useState<EroundCatalogue | null | undefined>(undefined);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Every saved mapping, for the stand-in decisions suggestions learn from; none when they cannot be read.
  const [mappings, setMappings] = useState<ModelMapping[]>([]);
  const [presets, setPresets] = useState<Record<string, Preset>>({});

  const loadMappings = useCallback(async () => {
    try {
      setMappings((await listModelMappings()) ?? []);
    } catch {
      setMappings([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setModels(await listUnmappedModels());
    } catch (err) {
      setModels([]);
      setNotice({ severity: "error", text: provisioningErrorMessage(err, "load") });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadMappings();
  }, [loadMappings]);

  useEffect(() => {
    void catalogueKeys().then(setInCatalogue);
  }, []);

  useEffect(() => {
    getEroundCatalogue()
      .then(setCatalogue)
      .catch(() => {
        setCatalogue(null);
        setCatalogueFailed(true);
      });
  }, []);

  useEffect(() => {
    if (!models?.length) return;
    let current = true;
    void loadCustomers(models).then((loaded) => {
      if (current) setCustomers(loaded);
    });
    return () => {
      current = false;
    };
  }, [models]);

  const onSaved = (result: Notice) => {
    setNotice(result);
    if (result.severity === "success") {
      void load();
      void loadMappings();
    }
  };

  return (
    <div>
      <p className="mt-0 text-600">
        Pick the manufacturer and model from eRound's own catalogue. Every station with the wallbox is created in
        eRound as the model picked here.
        {catalogue && <> eRound catalogue from {formatDay(catalogue.exportedAt)}.</>}
      </p>
      {catalogue === null && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text={
            catalogueFailed
              ? "eRound's catalogue could not be loaded, so no mapping can be saved here right now."
              : "eRound's catalogue has not been received yet, so no mapping can be saved here. The eRound worker sends it once a day."
          }
        />
      )}
      {notice && <Message severity={notice.severity} className="w-full mb-3" text={notice.text} />}
      {models?.length === 0 && <p>No wallbox model is blocking a station.</p>}
      {models?.map((model) => (
        <Card
          key={model.modelKey}
          className="mb-3"
          title={`${model.onboardingManufacturer} – ${model.onboardingModel}`}
          subTitle={<BlockedCustomers stations={model.blockedStations} customers={customers} />}
        >
          {inCatalogue.has(model.modelKey) ? (
            <p className="m-0">
              This model is in the Wallbox Catalogue, which keeps its eRound mapping.{" "}
              <Link to="/onboarding/wallbox">Map it in the Wallbox Catalogue</Link>
            </p>
          ) : (
            <>
              {catalogue && (
                <ModelSuggestions
                  // Starts again once the customer's specs arrive.
                  key={enteredHints(model, customers) ? "entered" : "name"}
                  initialHints={enteredHints(model, customers)}
                  wallbox={model}
                  catalogue={catalogue}
                  mappings={mappings}
                  onUse={(suggestion) =>
                    setPresets((prev) => ({
                      ...prev,
                      [model.modelKey]: { suggestion, seq: (prev[model.modelKey]?.seq ?? 0) + 1 },
                    }))
                  }
                />
              )}
              <MappingForm
                model={model}
                catalogue={catalogue ?? null}
                preset={presets[model.modelKey] ?? null}
                onSaved={onSaved}
              />
            </>
          )}
        </Card>
      ))}
    </div>
  );
};

export default UnmappedModelsPanel;
